// TraceCanvas: one canvas of traces. The trace it is anchored on is on the
// board, and so is what the reader lays on beside it: its agents' sessions,
// sessions it shares content with, any found by search, or the traces a
// chat's investigation rests on. Everything shares one clock, and the board
// carries the investigation of the canvas's chat: its claims on the cells
// they rest on, the sessions it looks into laid on as it finds them. What is
// on the canvas, and what the reader did there, are the canvas's own
// (lib/traceCanvasStore.js), so it mounts wherever a canvas is opened; the
// notes on its traces are the traces' own (lib/traceNoteStore.js).
import { memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import SessionCanvasView, { prefetchLaidOn } from "./SessionCanvasView.jsx";
import TraceLanesView, { BoardCellDock, turnsToJoinInputs } from "./TraceLanesView.jsx";
import { WorkspaceAddSheet, useTraceWorkspaceHost } from "./TraceWorkspaceHost.jsx";
import { chatSessionsToLay } from "../lib/canvasFollow.js";
import { query } from "../lib/dataClient.js";
import { fetchScopeReviews } from "../lib/scopeReviews.js";
import { getDockChatBar, subscribeDock } from "../lib/dockStore.js";
import { activateGraphChat, sendGraphReply } from "../lib/graphReplies.js";
import { activeGraphKey, graphVersion, readLiveGraph, subscribeGraph } from "../lib/investigationGraphRun.js";
import { searchStrings } from "../lib/sessionMatch.js";
import { applySessionPage, rejectSessionRecord, sessionPageRequest } from "../lib/sessionTabState.js";
import { unwrapCodeModeTurns } from "../lib/traceCodeModeCells.js";
import { grammarReady, warmGrammar } from "../lib/syntax/parserHost.js";
import { MAX_BOARD_SESSIONS, layOnTraceCanvas, setTraceCanvasSelection, useTraceCanvas } from "../lib/traceCanvasStore.js";
import { useWalkthrough, walkthroughChatKey, workspaceWalkKey } from "../lib/traceWalkthrough.js";
import { BoardWalkthrough } from "./Walkthrough.jsx";

const HOUR = 3600_000;
// How far back the analytics graph reads reviews.
const REVIEW_DAYS = 90;
const NO_TURNS = [];

const recordOf = (id, trace, turns, sessionId = id) => ({ status: "done", id, trace: { ...trace, sessionId }, turns: unwrapCodeModeTurns(turns) });
// The records the boards up now hold, by session (useTraceRecord).
const heldRecords = new Map();
const sessionOf = (trace) => String(trace?.sessionId || trace?.id || "").toLowerCase();

// A trace's record, read the way the trace page reads it: the session's
// newest page, its code-mode cells unwrapped once the grammar is up. A record
// the opener already holds (`handed`: the trace page read the session) is
// shown as it is, on the first frame once the grammar is up, and not read
// again. Either way the trace keeps its record's account (`record`: how much
// of it the page holds), so its turns are numbered from the record's start,
// as the trace page numbers them.
//
// The page before the oldest turn held is read when the reader asks for it
// (`loadEarlier`), one page at a time, as the trace page's "earlier" does: a
// long record is never read whole before the board draws.
//
// The trace's own facts (its tokens, clusters, where it ran from) take
// several reads; with a `range` its head (when it began, what it was, where
// it ran) takes one, and the record is read from that at once, shown as soon
// as it is in, the facts joining it when they arrive.
//
// A record another board on the page holds (a preview over the page, as it
// is mounted in a pane) is drawn on the first frame, as a handed one is, and
// not read again.
export function useTraceRecord(id, handed = null, range = null) {
  const given = handed && Array.isArray(handed.turns) && sessionOf(handed) === id ? handed : null;
  const [state, setState] = useState(() => {
    const held = !given && id ? heldRecords.get(id)?.state : null;
    if (held) return held;
    if (!given || !grammarReady("javascript")) return { status: "idle" };
    return recordOf(id, given, given.turns);
  });
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    if (state.status !== "done" || state.id !== id) return undefined;
    const mine = { state };
    heldRecords.set(id, mine);
    return () => { if (heldRecords.get(id) === mine) heldRecords.delete(id); };
  }, [id, state]);
  useEffect(() => {
    if (!id) {
      setState({ status: "idle" });
      return undefined;
    }
    let dead = false;
    if (!given && stateRef.current.status === "done" && stateRef.current.id === id) return () => { dead = true; };
    // The trace as known now: what its head says of it, until its own facts
    // are read.
    let traceNow = null;
    const settle = (trace, raw, sessionId) => setState(recordOf(id, trace, raw, sessionId));
    if (given) {
      if (!grammarReady("javascript")) {
        setState({ status: "loading", id });
        warmGrammar("javascript").catch(() => false).then(() => { if (!dead) settle(given, given.turns, id); });
      }
      return () => { dead = true; };
    }
    setState({ status: "loading", id });
    (async () => {
      try {
        // The record is read once, over the hour before the session's first
        // request as well: the trace is asked for without its own page.
        const traceRead = query("getTrace", { id, withoutRecord: true }, { cache: true });
        traceRead.catch(() => {});
        const heads = range != null ? await query("getSessionHeads", { ids: [id], range, whole: true }, { cache: true }).catch(() => null) : null;
        const head = heads?.[String(id).toLowerCase()] || null;
        // No head (a request's id, a session the range does not hold): the
        // record waits for the trace to say when it began.
        let lost = null;
        let full = Number.isFinite(head?.firstMs) ? null : await traceRead.catch((e) => { lost = e; return null; });
        if (!head?.firstMs && !full) {
          // Neither says when it began: the record is the session's own newest
          // page, as its card on the board reads it, the trace known from it.
          const page = await query("getSessionPage", { id, sinceMs: null }, { cache: true }).catch(() => null);
          const turns = Array.isArray(page?.turns) ? page.turns : [];
          if (!turns.length) throw lost || new Error("no such trace");
          await warmGrammar("javascript").catch(() => false);
          if (dead) return;
          const times = turns.map((t) => t?.messageAt).filter(Number.isFinite);
          const sessionId = String(id).toLowerCase();
          settle({
            id: sessionId, sessionId, summary: "", model: "", provider: "", endpointId: "",
            firstTimestamp: times.length ? Math.min(...times) : null, timestamp: times.length ? Math.max(...times) : null,
            turns, record: { session: true, loaded: turns.length, total: page.total ?? null, exhausted: Boolean(page.exhausted), nextCursor: page.nextCursor ?? null },
          }, turns, sessionId);
          return;
        }
        const sessionId = String(full?.sessionId || id).toLowerCase();
        const start = full ? full.firstTimestamp : head.firstMs;
        const since = Number.isFinite(start) ? start - HOUR : null;
        let raw = Array.isArray(full?.turns) ? full.turns : [];
        let account = null;
        try {
          const page = await query("getSessionPage", { id: sessionId, sinceMs: since }, { cache: true });
          if (Array.isArray(page?.turns) && page.turns.length >= raw.length) {
            raw = page.turns;
            account = { session: true, loaded: page.turns.length, total: page.total ?? null, exhausted: Boolean(page.exhausted), nextCursor: page.nextCursor ?? null };
          }
        } catch {
          // That read failed: the trace's own newest page stands, read now.
          const whole = await query("getTrace", { id }, { cache: true }).catch(() => null);
          if (Array.isArray(whole?.turns)) raw = whole.turns;
          full = full || whole;
        }
        await warmGrammar("javascript").catch(() => false);
        if (dead) return;
        traceNow = full || {
          id: sessionId, sessionId, summary: head.summary || "", model: head.model || "", provider: head.provider || "",
          endpointId: head.endpointId || "", firstTimestamp: head.firstMs, timestamp: Number.isFinite(head.lastMs) ? head.lastMs : head.firstMs,
        };
        settle({ ...traceNow, turns: raw, record: account }, raw, sessionId);
        if (full) return;
        // Its own facts, once read, join the record already shown.
        traceRead.then((t) => {
          if (dead || !t) return;
          traceNow = t;
          setState((cur) => (cur.status === "done" && cur.id === id ? { ...cur, trace: { ...t, endpointId: t.endpointId || cur.trace.endpointId, turns: cur.trace.turns, record: cur.trace.record, sessionId } } : cur));
        }, () => {});
      } catch (e) {
        if (!dead) setState({ status: "error", id, error: String(e?.message || e) });
      }
    })();
    return () => { dead = true; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- the handed record is read once, as the canvas opens
  // A session its head and its trace place on no machine (one the analytics
  // hold no head for) is placed where its review found it: each review report
  // keeps the machine it came from. Its header alone, no prose; reviews
  // complete after the session began, within the graph's 90 days.
  const unplaced = state.status === "done" && !state.trace?.endpointId ? state.id : null;
  useEffect(() => {
    if (!unplaced) return undefined;
    let dead = false;
    const t = stateRef.current.trace;
    const sid = String(t?.sessionId || unplaced).toLowerCase();
    const toMs = Date.now() + HOUR;
    const start = Number.isFinite(t?.firstTimestamp) ? t.firstTimestamp : Number.isFinite(t?.timestamp) ? t.timestamp : 0;
    fetchScopeReviews({ sessionIds: [sid], fromMs: Math.max(start - HOUR, toMs - REVIEW_DAYS * 24 * HOUR), toMs, sensitive: false, limit: 1 })
      .then((got) => {
        const ep = String((got?.rows || []).find((r) => r?.endpoint_id && String(r.trace_id || "").toLowerCase() === sid)?.endpoint_id || "");
        if (dead || !ep) return;
        setState((cur) => (cur.status === "done" && cur.id === unplaced && !cur.trace?.endpointId ? { ...cur, trace: { ...cur.trace, endpointId: ep } } : cur));
      }, () => {});
    return () => { dead = true; };
  }, [unplaced]);
  // The page before the oldest turn held, read on asking; what was read
  // stands if it cannot be, and its account says so.
  const loadEarlier = useCallback(async () => {
    const cur = stateRef.current;
    if (cur?.status !== "done" || cur.trace?.record?.loading) return;
    const data = { ...cur.trace, turns: cur.trace.turns || [] };
    const request = sessionPageRequest(data);
    if (!request?.cursor) return;
    const still = (s) => s.status === "done" && s.id === cur.id;
    const mark = (record) => setState((s) => (still(s) ? { ...s, trace: { ...s.trace, record } } : s));
    mark({ ...data.record, loading: true });
    try {
      const page = await query("getSessionPage", { id: request.sessionId, cursor: request.cursor, exclude: request.exclude, sinceMs: request.sinceMs, untilMs: request.untilMs, focalEventId: request.focalEventId });
      const next = applySessionPage({ data }, page, request).data;
      setState((s) => (still(s) ? recordOf(cur.id, { ...s.trace, turns: next.turns, record: next.record }, next.turns, s.trace.sessionId) : s));
    } catch (e) {
      mark(rejectSessionRecord({ data }, String(e?.message || e)).data.record);
    }
  }, []);
  return useMemo(() => ({ ...state, loadEarlier }), [state, loadEarlier]);
}

// A workspace anchored on no session (one the reader started from a note, a
// search, a machine, or sessions alike): the board alone, nothing read before
// it draws, every session on it laid on beside the rest.
// `needles`: what its searches look for, found in the sessions laid on and
// wired from one to the next. `walk`: the workspace's walkthrough, whose
// moments stand on it ({ key, walkKey }).
function LooseBoard({ canvas, graph, version, needles, walk = null }) {
  return (
    <SessionCanvasView
      walkthroughKey={walk?.key}
      walkKey={walk?.walkKey ?? null}
      trace={null}
      turns={NO_TURNS}
      model={null}
      needles={needles}
      turnsToJoinInputs={turnsToJoinInputs}
      cellDock={BoardCellDock}
      kg={graph?.nodes?.size ? { graph, version, overlay: null } : null}
      initialFocus={canvas.focus ?? null}
      onSelection={canvas.onSelection ?? null}
      publish={canvas.publish !== false}
      layered={canvas.layered}
      onLayer={canvas.onLayer}
      onUnlayer={canvas.onUnlayer}
      boardBar={canvas.bar}
      readings={canvas.readings}
      onReadings={canvas.onReadings}
      hidden={canvas.hidden}
      onHidden={canvas.onHidden}
      notes={canvas.notes}
      sizes={canvas.sizes}
      onSizes={canvas.onSizes}
      order={canvas.order}
      onOrder={canvas.onOrder}
      padMachines={canvas.machines ?? null}
      padSearches={canvas.searches ?? null}
      padNested={canvas.nested ?? null}
      nestedHomes={canvas.nestedHomes ?? null}
      onEnter={canvas.onEnter ?? null}
      onExit={canvas.onExit ?? null}
      flash={canvas.flash ?? null}
      range={canvas.range ?? null}
      workspaceId={canvas.workspaceId ?? null}
      nestInset={canvas.inset ?? null}
      titles={canvas.titles ?? null}
      pinnedFindings={canvas.pinnedFindings ?? null}
      looseNotes={canvas.looseNotes ?? null}
      onLooseNote={canvas.onLooseNote ?? null}
      onThingOff={canvas.onThingOff ?? null}
      onThingBack={canvas.onThingBack ?? null}
      onBranch={canvas.onBranch ?? null}
      onCellPick={canvas.onCellPick ?? null}
      onCardSelect={canvas.onCardSelect ?? null}
    />
  );
}

// Beside a workspace with no anchor while it is the board in front,
// headless: what the workspace's chat and the board's bar walk through every
// trace on it by (`sessions`), with what the chat it is worked in
// (`workspaceChat`) claimed. It lends the home's dock nothing: the reading,
// the walkthrough and the cases touching it are the chat's to show.
function PadDock({ canvas, walkKey, sessions, workspaceChat }) {
  const front = canvas.dock === "bridge" && !!canvas.dockFront;
  if (!front) return null;
  return <BoardWalkthrough key={walkKey} session={walkKey} sessions={sessions} workspaceChat={workspaceChat} board={canvas.workspaceId || null} />;
}

// A workspace with no anchor: its board, and beside it what its chat walks
// through it by. Its walkthrough reads every trace it holds, those inside the
// workspaces it holds too, each by the one here holding it (whose card its
// moments' pins stand on), however far in it is.
function PadBoard({ canvas, graph, version, needles, workspaceId }) {
  const walkKey = workspaceWalkKey(workspaceId || "");
  // Its moments stand while the workspace holds its walkthrough, and none a
  // walkthrough no longer kept left on the cards.
  const held = useWalkthrough(walkKey);
  const walkHeld = !!(held.account || held.notes.length);
  const walk = useMemo(() => ({ key: walkHeld ? walkthroughChatKey(walkKey) : null, walkKey }), [walkKey, walkHeld]);
  return (
    <>
      <LooseBoard canvas={canvas} graph={graph} version={version} needles={needles} walk={walk} />
      {workspaceId ? <PadDock canvas={canvas} walkKey={walkKey} sessions={canvas.walkTraces || []} workspaceChat={canvas.walkChat ?? null} /> : null}
    </>
  );
}

// A workspace inside another, drawn in its card on the outer board once the
// reader makes the card tall enough: a still picture of its sessions' cards
// (its anchor among them) and its notes, By time, filling the room it has
// (`width` wide until it is measured). Where the reader moved or sized the
// cards inside is not kept: a picture laid out that way runs wider than its
// room and is drawn small. Only to look at: going in is how to work there.
const NO_NEEDLES = [];
// `onPicture`: told where the picture drew each of its cards, in its own box.
// Drawn again only when what it shows or its room changes, not with every
// move on the board around it.
export const WorkspaceInset = memo(function WorkspaceInset({ workspaceId, range = null, width = 1100, onPicture = null }) {
  const c = useTraceCanvas(workspaceId);
  const host = useTraceWorkspaceHost(workspaceId, { dock: false, range });
  const layered = useMemo(() => (c.anchor ? [c.anchor, ...host.layered.filter((sid) => sid !== c.anchor)] : host.layered), [c.anchor, host.layered]);
  return (
    <SessionCanvasView
      trace={null}
      turns={NO_TURNS}
      model={null}
      needles={NO_NEEDLES}
      turnsToJoinInputs={turnsToJoinInputs}
      publish={false}
      layered={layered}
      readings={host.readings}
      hidden={host.hidden}
      notes={host.notes}
      pinnedFindings={host.pinnedFindings}
      looseNotes={host.looseNotes}
      titles={host.titles}
      range={range}
      still={width}
      onPicture={onPicture}
    />
  );
});

// The investigation a canvas carries. A chat's canvas carries that chat's; a
// trace's, the chat it is worked in, while that chat is the one in its dock
// (the active chat): until the canvas has its chat, the chat in front is
// another investigation's, whose sessions are not this canvas's to follow.
// Which chat is active has no event of its own, so its key is read again
// whenever the dock's chats change and on a slow beat.
function useCanvasGraphKey(canvasId, chatId) {
  const own = canvasId?.startsWith("chat:") ? canvasId.slice("chat:".length) : null;
  const chatBar = useSyncExternalStore(subscribeDock, getDockChatBar, () => null);
  const paired = !own && chatId != null && (chatBar?.tabs || []).some((t) => t.active && String(t.id) === chatId);
  const [active, setActive] = useState(null);
  useEffect(() => {
    if (!paired) { setActive(null); return undefined; }
    const read = () => setActive(activeGraphKey());
    read();
    const t = setInterval(read, 1500);
    return () => clearInterval(t);
  }, [paired, chatBar]);
  return own || (paired ? active : null);
}

// A chat's investigation graph, live: a running chat writes it after every
// call.
function useChatGraph(graphKey) {
  const subscribe = useCallback((cb) => (graphKey ? subscribeGraph(graphKey, cb) : () => {}), [graphKey]);
  const version = useSyncExternalStore(subscribe, () => (graphKey ? graphVersion(graphKey) : 0), () => 0);
  const graph = useMemo(() => (graphKey ? readLiveGraph(graphKey) : null), [graphKey, version]); // eslint-disable-line react-hooks/exhaustive-deps
  return { graph, version };
}

// `onAddRequest`: + Add finds somewhere of the host's (the home's list)
// instead of bringing up the add sheet.
// `dockBridge`: the board is drawn in a workspace with a dock (WorkspaceDock:
// the home's panes, or a workspace over the page), which holds the chat; the
// board lends that dock its tabs (a trace's reading and the cells picked on
// it) while it is the board in front (`dockFront`). Without one, a board
// opens a dock of its own only to read a cell picked on it.
// `onCellPick`: where a cell picked on a board with no trace's reading behind
// it is read (the workspace's dock), in place of the reading beside its card;
// `onCardSelect`, where the trace last selected on the board is (its Reading
// there). `emptyBoard`: what the board shows with nothing on it yet, in place
// of its own word (the deck's way to start one).
// `barLead` and `barTail`: the host's cells in the board's bar; `addOpen`,
// whether what + Add brings up (the host's) is up. `framed`: the host draws
// no frame of its own, so the record's reading is framed as the board is.
// `machines`, `searches` and `nested`: the workspace's machines, searches
// and inner workspaces with their sessions, as the host reads them
// (useTraceWorkspaceHost); `onEnter` and `onExit` go into an inner one and
// back out. `flash`: a card to point out ({ key, n }, `n` telling one asking
// from the next), as the one just come back out of. `onClear` and
// `clearLabel`: what the bar's Clear does and says (useTraceWorkspaceHost).
// `publish` false: the board tells the page nothing about itself (a preview).
export default function TraceCanvas({ canvasId, trace = null, focus = null, range = "1M", onRangeChange = null, onTitle = null, onAddRequest = null, dockBridge = false, dockFront = false, onCellPick = null, onCardSelect = null, emptyBoard = null, barLead = null, barTail = null, addOpen = false, framed = false, machines = null, searches = null, nested = null, onEnter = null, onExit = null, flash = null, onClear = null, clearLabel = null, publish = true }) {
  const c = useTraceCanvas(canvasId);
  const anchor = c.anchor;
  const record = useTraceRecord(anchor, trace, range);
  // The board draws once its anchor's record is in; the sessions laid on
  // beside it are read meanwhile, so it finds them read.
  const laidIds = useMemo(() => (c.layered || []).map((t) => String(t?.id || "").toLowerCase()).filter((sid) => sid && sid !== String(anchor || "").toLowerCase()), [c.layered, anchor]);
  const laidKey = laidIds.join(",");
  useEffect(() => {
    if (!anchor || !laidIds.length) return undefined;
    const ctl = new AbortController();
    prefetchLaidOn(laidIds, range, { signal: ctl.signal });
    return () => ctl.abort();
  }, [anchor, laidKey, range]); // eslint-disable-line react-hooks/exhaustive-deps
  const graphKey = useCanvasGraphKey(canvasId, c.chatId);
  const { graph, version } = useChatGraph(graphKey);
  // A canvas follows its chat: the sessions the chat's claims rest on are
  // laid on as they land, as many as the board has room for (never pushing
  // one off), except the ones the reader took off.
  useEffect(() => {
    if (!graph?.nodes?.size || !c.follow) return;
    const on = new Set([c.anchor, ...c.layered.map((t) => t.id)].filter(Boolean));
    const room = MAX_BOARD_SESSIONS - on.size;
    if (room <= 0) return;
    const off = new Set(c.dismissed);
    const fresh = chatSessionsToLay(graph).filter((s) => !on.has(s.id) && !off.has(s.id)).slice(0, room);
    if (fresh.length) layOnTraceCanvas(canvasId, fresh.map((s) => ({ id: s.id })));
  }, [graph, version, canvasId, c.follow, c.anchor, c.layered, c.dismissed]);
  // With no anchor, the strings its searches look for join its sessions.
  const padNeedles = useMemo(() => [...new Set((c.searches || []).flatMap((q) => searchStrings(q.q)))].map((needle) => ({ needle, cells: [] })), [c.searches]);
  const replyToGraph = useCallback((reply) => sendGraphReply(graphKey, reply), [graphKey]);
  const openGraphChat = useCallback(() => activateGraphChat(graphKey), [graphKey]);
  const summary = record.status === "done" ? String(record.trace?.summary || "") : "";
  useEffect(() => {
    if (summary) onTitle?.(summary);
  }, [summary]); // eslint-disable-line react-hooks/exhaustive-deps

  // The add sheet, docked on the canvas's right side; the canvas keeps what it
  // shows clear of it.
  const [adding, setAdding] = useState(false);
  const onAdd = useCallback(() => (onAddRequest ? onAddRequest() : setAdding((a) => !a)), [onAddRequest]);
  const closeSheet = useCallback(() => setAdding(false), []);
  const [cover, setCover] = useState(0);
  // The trace a canvas is anchored on stays its anchor: no card is made the
  // anchor instead. Its cells read in the workspace's dock, beside its chat
  // (the analytics chat, told what is selected on the board) and the trace's
  // reading.
  const canvas = useTraceWorkspaceHost(canvasId, {
    dock: dockBridge ? "bridge" : false, dockFront, onCellPick, onCardSelect, empty: emptyBoard, focus, onSelection: setTraceCanvasSelection, canFollow: !!graph?.nodes?.size, adding: onAddRequest ? addOpen : adding, onAdd, cover, lead: barLead, tail: barTail, machines, searches, nested, onEnter, onExit, flash, range, workspaceId: canvasId,
    inset: WorkspaceInset, onClear, clearLabel, publish,
  });

  // While the anchor's record is read, the board draws its sessions anyway,
  // the anchor among them, each from its head, its bar saying the record is
  // being read; once the record is in the workspace's own board takes its
  // place, every card gliding from where this one drew it. A
  // record the trace page handed over is shown as it is, nothing read.
  const handedHere = !!trace && Array.isArray(trace.turns) && sessionOf(trace) === anchor;
  const readingSaid = "Reading the trace's record...";
  const meanwhile = useMemo(() => (anchor && !handedHere ? {
    ...canvas,
    layered: [anchor, ...(canvas.layered || []).filter((sid) => sid !== anchor)],
    onUnlayer: (sid) => (sid === anchor ? null : canvas.onUnlayer?.(sid)),
    bar: canvas.bar ? { ...canvas.bar, status: readingSaid } : canvas.bar,
  } : null), [canvas, anchor, handedHere, readingSaid]);
  if (!anchor) {
    return (
      <div className="relative flex h-full min-h-0 min-w-0 flex-col" data-trace-canvas="" data-loose-board="">
        <PadBoard canvas={canvas} graph={graph} version={version} needles={padNeedles} workspaceId={canvasId} />
        <WorkspaceAddSheet open={adding} onClose={closeSheet} onCover={setCover} canvasId={canvasId} range={range} onRangeChange={onRangeChange} title={c.title} />
      </div>
    );
  }
  // Meanwhile, and when the record cannot be read, the board draws its
  // sessions anyway, the workspace's dock and the chat in it beside it.
  if (record.status !== "done" && meanwhile) {
    const board = record.status === "error" && meanwhile.bar
      ? { ...meanwhile, bar: { ...meanwhile.bar, status: `The trace's record could not be read (${record.error}).` } }
      : meanwhile;
    const loose = <LooseBoard canvas={board} graph={graph} version={version} needles={padNeedles} />;
    return (
      <div className="relative flex h-full min-h-0 min-w-0 flex-col" data-trace-canvas="" data-board-meanwhile="">
        {loose}
        <WorkspaceAddSheet open={adding} onClose={closeSheet} onCover={setCover} canvasId={canvasId} range={range} onRangeChange={onRangeChange} title={c.title} />
      </div>
    );
  }
  if (record.status !== "done") {
    const said = record.status === "error" ? `The trace's record could not be read (${record.error}).` : readingSaid;
    // Framed by its host, it keeps the host's cells in a bar of its own
    // meanwhile, as the board will.
    if (framed && (barLead || barTail)) {
      return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)]" data-trace-canvas="">
          <div className="grid min-h-0 flex-1 place-items-center px-[24px] text-center text-[12px] text-[color:var(--muted)]">{said}</div>
          <div className="flex h-[32px] min-w-0 flex-none items-stretch border-t border-[color:var(--line-soft)] text-[11px]" data-canvas-controls="">
            <div className="flex min-w-0 flex-1 items-stretch">{barLead}</div>
            <div className="flex flex-none items-stretch">{barTail}</div>
          </div>
        </div>
      );
    }
    return (
      <div className={`grid h-full min-h-0 place-items-center text-[12px] text-[color:var(--muted)] ${framed ? "rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)]" : ""}`} data-trace-canvas="">
        {said}
      </div>
    );
  }
  return (
    <div className="relative flex h-full min-h-0 min-w-0 flex-col" data-trace-canvas="">
      <TraceLanesView
        key={record.id}
        trace={record.trace}
        turns={record.turns}
        record={record.trace.record || null}
        onLoadEarlier={record.trace.record?.session ? record.loadEarlier : null}
        traceTimestamp={record.trace.timestamp}
        fill
        canvas={canvas}
        graphKey={graphKey}
        onGraphReply={graphKey ? replyToGraph : null}
        onOpenGraphChat={graphKey ? openGraphChat : null}
      />
      <WorkspaceAddSheet open={adding} onClose={closeSheet} onCover={setCover} canvasId={canvasId} range={range} onRangeChange={onRangeChange} title={summary} />
    </div>
  );
}
